<template>
    <div
        role="row"
        tabindex="0"
        class="ds-employees-grid ds-employees-row"
        :class="archive ? 'ds-employees-grid-archive' : 'ds-employees-grid-active'"
        :aria-label="`Открыть карточку: ${name}`"
        @click="emit('user')"
        @keydown.enter.self.prevent="emit('user')"
        @keydown.space.self.prevent="emit('user')"
    >
        <div role="cell" class="ds-employees-person">
            <Avatar
                class="ds-employees-avatar"
                :class="{ 'is-muted': archive }"
                :first-name="firstName"
                :last-name="lastName"
                :url="avatar"
                width="36"
                height="36"
            />
            <div class="ds-employees-person-text">
                <span class="ds-employees-name ds-employees-ellipsis" :title="name">{{ name }}</span>
                <span class="ds-employees-position ds-employees-ellipsis" :title="position">{{ position }}</span>
            </div>
        </div>
        <div role="cell" class="ds-employees-cell ds-employees-ellipsis" :title="department">{{ department }}</div>
        <div v-if="archive" role="cell" class="ds-employees-cell ds-employees-numeric">{{ fired }}</div>
        <div v-else role="cell" class="ds-employees-cell ds-employees-hours">
            <span v-if="hoursBar" class="ds-employees-hours-track" aria-hidden="true">
                <span
                    class="ds-employees-hours-fill"
                    :class="{ 'is-low': hoursBar.isLow }"
                    :style="{ width: `${hoursBar.percent}%` }"
                ></span>
            </span>
            <span class="ds-employees-hours-text">{{ hoursText }}</span>
        </div>
        <div v-if="!archive" role="cell" class="ds-employees-cell">
            <span v-if="violations === null">—</span>
            <span
                v-else
                class="ds-employees-violations"
                :class="{ 'is-none': violations === 0 }"
            >{{ violations === 0 ? 'нет' : violations }}</span>
        </div>
        <div role="cell" class="ds-employees-cell">
            <RatingStars v-if="rating !== null" :rating="rating" size="sm" />
            <span v-else>—</span>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    employee: EmployeeRow,
    archive: boolean
}
import { computed } from 'vue'
import dayjs from 'dayjs'
import Avatar from '../Avatar.vue'
import RatingStars from '@/ui/RatingStars.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import type { EmployeeRow } from './employeesList'
import { formatHours, getHoursBar, readEmployeeHours } from './employeeHours'
import { readViolationsCount } from './employeeViolations'

const emit = defineEmits(['user'])
const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()
const { employee, archive } = defineProps<Props>()

// Как и раньше — первое трудоустройство; пустой массив/отсутствие не ломают строку.
const employment = computed(() => employee.employments?.[0] ?? null)

// Рейтинг — User.rating строки (RD-154); прежний employments[0].rating — только если его нет.
const rating = computed(() => employee.rating ?? employment.value?.rating ?? null)

const firstName = computed(() => employee.first_name ?? '')
const lastName = computed(() => employee.last_name ?? '')
const avatar = computed(() => employee.avatar ?? undefined)
const name = computed(() => [employee.last_name, employee.first_name, employee.middle_name].filter(Boolean).join(' ') || '—')

const department = computed(() => {
    const id = employment.value?.department
    return (id && departmentStore.departments.find(dep => dep.id === id)?.name) || '—'
})
const position = computed(() => {
    const id = employment.value?.position
    return (id && scheduleStore.positions.find(pos => pos.id === id)?.name) || '—'
})

const fired = computed(() => {
    const date = employee.last_fired_at ? dayjs(employee.last_fired_at) : null
    return date?.isValid() ? date.format('DD.MM.YYYY') : '—'
})

const hours = computed(() => readEmployeeHours(employee))
const hoursBar = computed(() => getHoursBar(hours.value))
const hoursText = computed(() => {
    const { worked, norm } = hours.value
    if (worked === null && norm === null) return '—'
    return `${formatHours(worked)} / ${formatHours(norm)} ч`
})

// Готовый итог backend (violations_count); null — поля ещё нет или значение некорректно.
const violations = computed(() => archive ? null : readViolationsCount(employee.violations_count))
</script>
